import { Component, OnInit } from '@angular/core';
import { OrganizationType } from 'src/app/core/data-models/organization-type.model';
import { ORGANIZATION_TYPE_OPTIONS } from 'src/app/shared/constants/organization-type-options';
import { getOrganizationTypeLabel } from 'src/app/core/utils/organization-type-utils';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { LookupUserService } from '../../core/api-services/lookup-user.service';
import { UsersOrganizationsService } from '../../core/api-services/users-organizations.service';
import { OrganizationService } from '../../core/data-services/organization.service';
import { BillingResponsibility } from '../../core/data-models/billing-responsibility.model';
import { MembershipRoles } from '../../core/data-models/membership-roles.model';
import { Organization } from '../../core/data-models/organization.model';
import { CreateUserOrganizationRequest } from '../../core/request-models/create-user-organization-request.model';
import { OrganizationRequest } from '../../core/request-models/organization-request.model';
import { ExistingUser } from '../../core/response-models/existing-user.model';
import { existingUserFromError } from '../../core/utils/existing-user-error-utils';
import { optionalText } from '../../core/utils/form-value-utils';
import { NgxMaskDirective } from 'ngx-mask';
import { toNationalDigits } from 'src/app/shared/utils/format.utils';
import { UsStateSelect } from '../../shared/components/app-us-state-select/app-us-state-select';

@Component({
  selector: 'app-organization-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgxMaskDirective, UsStateSelect],
  templateUrl: './organization-form.component.html',
  styleUrl: './organization-form.component.scss'
})
export class OrganizationFormComponent implements OnInit {
  organizationForm: FormGroup;
  loading = false;
  loadingOrganization = false;
  errorMessage = '';
  successMessage = '';

  isEditMode = false;
  organizationId: string | null = null;

  // Dropdown options
  organizationTypes = ORGANIZATION_TYPE_OPTIONS;

  billingOptions = [
    { value: 'self', label: 'Organization pays for itself' },
    { value: 'parent', label: 'Parent organization pays' }
  ];

  // Organization types that require EIN (per database constraint)
  einRequiredTypes = [OrganizationType.Llc, OrganizationType.SCorp, OrganizationType.CCorp, OrganizationType.Partnership];
  // Organization types that require SSN (per database constraint)
  ssnRequiredTypes = [OrganizationType.Individual];
  // Organization types that show EIN field (optional for some)
  einVisibleTypes = [OrganizationType.Llc, OrganizationType.SCorp, OrganizationType.CCorp, OrganizationType.Partnership, OrganizationType.NonProfit];
  // Organization types that show SSN field (optional for some)
  ssnVisibleTypes = [OrganizationType.Individual, OrganizationType.SoleProprietorship];

  /** Create mode: the owner is an existing user (looked up by email) or a new user. */
  ownerMode: 'new' | 'existing' = 'new';
  ownerForm: FormGroup;
  existingOwner: ExistingUser | null = null;
  lookingUpOwner = false;
  ownerLookupMessage = '';

  /** Edit mode: the tax ids as loaded, so a blank field the hub never sent is not sent back as a clear. */
  private loadedTaxIds: { ein?: string | null; ssn?: string | null } = {};

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private organizationService: OrganizationService,
    private usersOrganizationsService: UsersOrganizationsService,
    private lookupUserService: LookupUserService
  ) {
    this.ownerForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      firstName: [''],
      lastName: [''],
    });
    this.organizationForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      organizationType: [OrganizationType.Llc, Validators.required],
      ein: [''],
      ssn: [''],
      streetAddressLine1: ['', Validators.required],
      streetAddressLine2: [''],
      city: ['', Validators.required],
      state: ['', Validators.required],
      zipCode: ['', [Validators.required, Validators.pattern(/^\d{5}(-\d{4})?$/)]],
      country: ['USA', Validators.required],
      latitude:  ['', Validators.pattern(/^[-+]?(90(\.0+)?|[1-8]?\d(\.\d+)?)$/)],
      longitude: ['', Validators.pattern(/^[-+]?(180(\.0+)?|(1[0-7]\d|[1-9]?\d)(\.\d+)?)$/)],
      email: ['', [Validators.email]],
      phone: ['', [Validators.pattern(/^\+?[\d\s\-()]+$/)]],
      billingResponsibility: ['self', Validators.required],
      parentOrganizationId: ['']
    });
  }

  ngOnInit(): void {
    // Check if we're in edit mode
    this.organizationId = this.route.snapshot.paramMap.get('id');
    this.isEditMode = !!this.organizationId && this.organizationId !== 'create';

    // Listen for organization type changes to update EIN/SSN validation
    this.organizationForm.get('organizationType')?.valueChanges.subscribe(type => {
      this.updateTaxIdValidators(type);
    });

    // Set initial validation based on default type
    this.updateTaxIdValidators(this.organizationForm.get('organizationType')?.value);

    if (this.isEditMode) {
      this.loadOrganization();
    }
  }

  /**
   * Update EIN/SSN validators based on organization type
   */
  private updateTaxIdValidators(orgType: OrganizationType | null | undefined): void {
    const einControl = this.organizationForm.get('ein');
    const ssnControl = this.organizationForm.get('ssn');

    // Clear existing validators
    einControl?.clearValidators();
    ssnControl?.clearValidators();

    // EIN validation: required for business entities, pattern always applies
    if (this.einRequiredTypes.includes(orgType as OrganizationType)) {
      einControl?.setValidators([Validators.required, Validators.pattern(/^\d{2}-?\d{7}$/)]);
    } else if (this.einVisibleTypes.includes(orgType as OrganizationType)) {
      einControl?.setValidators([Validators.pattern(/^\d{2}-?\d{7}$/)]);
    }

    // SSN validation: required for individuals, pattern always applies
    if (this.ssnRequiredTypes.includes(orgType as OrganizationType)) {
      ssnControl?.setValidators([Validators.required, Validators.pattern(/^\d{3}-?\d{2}-?\d{4}$/)]);
    } else if (this.ssnVisibleTypes.includes(orgType as OrganizationType)) {
      ssnControl?.setValidators([Validators.pattern(/^\d{3}-?\d{2}-?\d{4}$/)]);
    }

    // Update validity state
    einControl?.updateValueAndValidity();
    ssnControl?.updateValueAndValidity();
  }

  // Computed properties for template
  get showEinField(): boolean {
    const orgType = this.organizationForm.get('organizationType')?.value;
    return this.einVisibleTypes.includes(orgType as OrganizationType);
  }

  get showSsnField(): boolean {
    const orgType = this.organizationForm.get('organizationType')?.value;
    return this.ssnVisibleTypes.includes(orgType as OrganizationType);
  }

  get einRequired(): boolean {
    const orgType = this.organizationForm.get('organizationType')?.value;
    return this.einRequiredTypes.includes(orgType as OrganizationType);
  }

  get ssnRequired(): boolean {
    const orgType = this.organizationForm.get('organizationType')?.value;
    return this.ssnRequiredTypes.includes(orgType as OrganizationType);
  }

  async loadOrganization(): Promise<void> {
    this.loadingOrganization = true;

    try {
      const org = await firstValueFrom(this.organizationService.getOrganization(this.organizationId!));
      if (org) {
        this.patchForm(org);
      }
    } catch (error) {
      console.error('Failed to load organization:', error);
      this.errorMessage = 'Failed to load organization details';
    } finally {
      this.loadingOrganization = false;
    }
  }

  /**
   * Edit mode reads the full `ein` / `ssn` from the detail until the audited tax-id endpoint exists
   * (ADMIN-READABLE.md). They are remembered so a blank field the hub never sent is not sent back as a clear.
   */
  private patchForm(org: Organization): void {
    this.loadedTaxIds = { ein: org.ein, ssn: org.ssn };
    this.organizationForm.patchValue({
      name: org.name ?? '',
      organizationType: org.organizationType,
      ein: org.ein || '',
      ssn: org.ssn || '',
      streetAddressLine1: org.streetAddressLine1 ?? '',
      streetAddressLine2: org.streetAddressLine2 || '',
      city: org.city ?? '',
      state: org.state ?? '',
      zipCode: org.zipCode ?? '',
      country: org.country ?? '',
      longitude: org.longitude ?? '',
      latitude: org.latitude ?? '',
      email: org.email || '',
      phone: toNationalDigits(org.phone),
      billingResponsibility: org.billingResponsibility ?? 'self'
    });
    this.updateTaxIdValidators(org.organizationType);
  }

  async onSubmit(): Promise<void> {
    const ownerInvalid = !this.isEditMode && !this.ownerReady;
    if (this.organizationForm.invalid || ownerInvalid) {
      this.organizationForm.markAllAsTouched();
      this.ownerForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      if (this.isEditMode) {
        await this.updateOrganization();
      } else {
        await this.createOrganization();
      }
    } catch (error: unknown) {
      console.error('Save organization error:', error);
      this.handleSaveError(error);
    } finally {
      this.loading = false;
    }
  }

  /** PUT /admin/organizations/:id with the admin-writable fields from the form. */
  private async updateOrganization(): Promise<void> {
    await firstValueFrom(this.organizationService.updateOrganization(this.organizationId!, this.toUpdateRequest()));
    this.successMessage = 'Organization updated successfully';

    // Navigate back to detail view after short delay
    setTimeout(() => {
      this.router.navigate(['/organizations', this.organizationId]);
    }, 1500);
  }

  /**
   * Creates the organization with its owner in ONE POST /admin/users-organizations (never the user route
   * POST /organizations), so a failure can't leave a half-created organization for a retry to duplicate.
   * Billing is always 'self' on create; it can be changed afterwards in edit mode.
   */
  private async createOrganization(): Promise<void> {
    const formData = this.organizationForm.value;
    const created = await firstValueFrom(this.usersOrganizationsService.create(this.toCreateRequest()));

    this.router.navigate(['/success'], {
      state: {
        entities: [
          { type: 'organization', action: 'created', name: formData.name },
          { type: 'user', action: this.ownerMode === 'new' ? 'created' : 'existing', name: this.ownerLabel },
        ],
        message: 'Organization created successfully',
        details: {
          'Type': getOrganizationTypeLabel(formData.organizationType),
          'Location': `${formData.city}, ${formData.state}`,
          'Owner': this.ownerLabel,
        },
        createAnotherRoute: '/organizations/create',
        additionalActions: [
          { label: 'Continue Setup', route: `/organizations/${created.organizationId}/setup` },
          { label: 'View Organization', route: `/organizations/${created.organizationId}` },
        ],
      }
    });
  }

  /** The body for POST /admin/users-organizations: the owner (new or existing) and the new organization. */
  private toCreateRequest(): CreateUserOrganizationRequest {
    const formData = this.organizationForm.value;
    const owner = this.ownerForm.value;
    const ownerFields = this.ownerMode === 'existing' && this.existingOwner
      ? { existingProfileId: this.existingOwner.id }
      : { email: owner.email.trim(), firstName: optionalText(owner.firstName) ?? undefined, lastName: optionalText(owner.lastName) ?? undefined };
    return {
      role: MembershipRoles.Owner,
      ...ownerFields,
      organizationName: formData.name.trim(),
      organizationType: formData.organizationType,
      ein: this.showEinField ? optionalText(formData.ein) ?? undefined : undefined,
      ssn: this.showSsnField ? optionalText(formData.ssn) ?? undefined : undefined,
      streetAddress: formData.streetAddressLine1.trim(),
      streetAddressLine2: optionalText(formData.streetAddressLine2) ?? undefined,
      city: formData.city.trim(),
      state: formData.state,
      zipCode: formData.zipCode.trim(),
      country: optionalText(formData.country) ?? undefined,
      organizationEmail: optionalText(formData.email) ?? undefined,
      organizationPhone: optionalText(formData.phone) ?? undefined,
    };
  }

  /** The PUT body: every admin-writable field on the form. Emptied optional fields are sent as `null`. */
  private toUpdateRequest(): OrganizationRequest {
    const formData = this.organizationForm.value;
    return {
      name: formData.name.trim(),
      organizationType: formData.organizationType,
      ein: this.showEinField ? this.taxIdValue(formData.ein, this.loadedTaxIds.ein) : undefined,
      ssn: this.showSsnField ? this.taxIdValue(formData.ssn, this.loadedTaxIds.ssn) : undefined,
      streetAddressLine1: formData.streetAddressLine1.trim(),
      streetAddressLine2: optionalText(formData.streetAddressLine2),
      city: formData.city.trim(),
      state: formData.state,
      zipCode: formData.zipCode.trim(),
      country: formData.country.trim(),
      email: optionalText(formData.email),
      phone: optionalText(formData.phone),
      billingResponsibility: formData.billingResponsibility as BillingResponsibility,
    };
  }

  /** A blank tax id clears the stored one, unless the hub never sent it (then it is left unchanged). */
  private taxIdValue(value: string, loaded: string | null | undefined): string | null | undefined {
    const text = optionalText(value);
    if (text === null && loaded === undefined) {
      return undefined;
    }
    return text;
  }

  private handleSaveError(error: unknown): void {
    const existing = existingUserFromError(error);
    if (existing) {
      // USER_ALREADY_EXISTS: offer the existing user as the owner instead.
      this.ownerMode = 'existing';
      this.existingOwner = existing;
      this.errorMessage = 'A user with this email already exists. They have been selected as the owner; submit again to continue.';
      return;
    }
    const fallback = this.isEditMode
      ? 'Failed to update organization. Please try again.'
      : 'Failed to create organization. Please try again.';
    this.errorMessage = ApiError.fromHttpError(error).errors[0] ?? fallback;
  }

  // =========================================
  // Owner picker (create mode)
  // =========================================

  setOwnerMode(mode: 'new' | 'existing'): void {
    this.ownerMode = mode;
    this.existingOwner = null;
    this.ownerLookupMessage = '';
  }

  /** GET /admin/lookup-user for the owner email. */
  async lookupOwner(): Promise<void> {
    const email = (this.ownerForm.get('email')?.value ?? '').trim();
    if (!email) return;

    this.lookingUpOwner = true;
    this.existingOwner = null;
    this.ownerLookupMessage = '';
    try {
      this.existingOwner = await firstValueFrom(this.lookupUserService.lookupUser(email));
    } catch (error: unknown) {
      const apiError = ApiError.fromHttpError(error);
      this.ownerLookupMessage = apiError.status === 404 ? 'No user found with this email.' : apiError.message;
    } finally {
      this.lookingUpOwner = false;
    }
  }

  /** Whether the owner section is complete: a found user, or a valid new-user email. */
  get ownerReady(): boolean {
    return this.ownerMode === 'existing' ? !!this.existingOwner : this.ownerForm.valid;
  }

  get ownerLabel(): string {
    const source = this.ownerMode === 'existing' && this.existingOwner ? this.existingOwner : this.ownerForm.value;
    const name = `${source.firstName || ''} ${source.lastName || ''}`.trim();
    return name || source.email || '';
  }

  cancel(): void {
    if (this.isEditMode) {
      this.router.navigate(['/organizations', this.organizationId]);
    } else {
      this.router.navigate(['/organizations']);
    }
  }

  get pageTitle(): string {
    return this.isEditMode ? 'Edit Organization' : 'Create Organization';
  }

  get submitButtonText(): string {
    if (this.loading) {
      return this.isEditMode ? 'Saving...' : 'Creating...';
    }
    return this.isEditMode ? 'Save Changes' : 'Create Organization';
  }
}
